import { SetterConfig, StyleValueKey } from '../types';
import { CompRef } from '@wix/document-services-types';
import { CssPropertyType, StyleValues } from '../../../types';
import { CssCustomProperties } from '@wix/component-protocol/schema';
import {
  decompose,
  isShorthandProperty,
} from '../../../../../apis/services/editActionsServices/nativeActions/globalDesign/common/decomposer';
import { compose } from '../../../../../apis/services/editActionsServices/nativeActions/globalDesign/common/composer';
import { ManifestComponentExtensionType } from '@wix/editor-platform-manifest-services';
import {
  STYLE_NAMESPACE,
  StyleNamespace,
} from '../../../../../apis/services/editActionsServices/nativeActions/globalDesign/types';
import { VariableResolver } from '../../abstractStyles/variableResolver';
import type { EditorPointer } from '@wix/editor-document-logic-api';

type StyleChange = {
  styleValue: string;
  targetKey: string;
} | null;

const getStyleСhange = (
  newValue: string,
  valueCssPropertyType: CssPropertyType,
  setterConfig: SetterConfig,
  namespace: StyleValues[keyof StyleValues],
  themeVariables: Record<string, string> = {},
): StyleChange => {
  if (
    setterConfig.source.type === 'whole' &&
    setterConfig.target.type === 'whole'
  ) {
    return { styleValue: newValue, targetKey: setterConfig.target.styleItemId };
  }

  if (
    setterConfig.source.type === 'sub' &&
    setterConfig.target.type === 'whole'
  ) {
    let resolvedValue = newValue;

    if (
      isShorthandProperty(valueCssPropertyType) &&
      VariableResolver.isSingleCSSVariable(newValue)
    ) {
      resolvedValue = VariableResolver.resolveVariableRecursively(
        newValue,
        themeVariables,
      );
    }

    const decomposed = decompose(valueCssPropertyType, resolvedValue);
    const styleValue = decomposed?.[setterConfig.source.subKey];

    return styleValue
      ? { styleValue, targetKey: setterConfig.target.styleItemId }
      : null;
  }

  if (
    setterConfig.source.type === 'whole' &&
    setterConfig.target.type === 'sub'
  ) {
    if (setterConfig.target.styleNamespace === STYLE_NAMESPACE.CssProperties) {
      // source:whole means newValue is already atomic, use as-is
      return {
        styleValue: newValue,
        targetKey: setterConfig.target.subKey,
      };
    }

    if (
      setterConfig.target.styleNamespace === STYLE_NAMESPACE.CssCustomProperties
    ) {
      // For CssCustomProperties, compose atomic value with old value
      const allValues = setterConfig.target.engine.getAllValues();
      const cssCustomProperties =
        allValues.mode === 'cssCustomProperties' || allValues.mode === 'mixed'
          ? allValues.raw.cssCustomProperties
          : {};

      const oldValue =
        namespace[setterConfig.target.styleItemId] ||
        cssCustomProperties[setterConfig.target.styleItemId];

      if (!oldValue) {
        return null;
      }

      const decomposed = decompose(
        setterConfig.target.cssPropertyType,
        oldValue,
      );

      if (!decomposed) {
        return null;
      }

      decomposed[setterConfig.target.subKey] = newValue;

      const composed = compose(setterConfig.target.cssPropertyType, decomposed);

      return composed
        ? { styleValue: composed, targetKey: setterConfig.target.styleItemId }
        : null;
    }
  }

  if (
    setterConfig.source.type === 'sub' &&
    setterConfig.target.type === 'sub'
  ) {
    if (setterConfig.target.styleNamespace === STYLE_NAMESPACE.CssProperties) {
      // Decompose source and extract the atomic value
      const decomposed = decompose(valueCssPropertyType, newValue);
      const atomicValue = decomposed?.[setterConfig.source.subKey];

      return atomicValue
        ? { styleValue: atomicValue, targetKey: setterConfig.target.subKey }
        : null;
    }

    if (
      setterConfig.target.styleNamespace === STYLE_NAMESPACE.CssCustomProperties
    ) {
      // First extract atomic value from source
      let sourceResolvedValue = newValue;

      if (
        isShorthandProperty(valueCssPropertyType) &&
        VariableResolver.isSingleCSSVariable(newValue)
      ) {
        sourceResolvedValue = VariableResolver.resolveVariableRecursively(
          newValue,
          themeVariables,
        );
      }

      const sourceDecomposed = decompose(
        valueCssPropertyType,
        sourceResolvedValue,
      );
      const atomicValue = sourceDecomposed?.[setterConfig.source.subKey];

      if (!atomicValue) {
        return null;
      }

      const allValues = setterConfig.target.engine.getAllValues();
      const cssCustomProperties =
        allValues.mode === 'cssCustomProperties' || allValues.mode === 'mixed'
          ? allValues.raw.cssCustomProperties
          : {};

      const oldValue =
        namespace[setterConfig.target.styleItemId] ||
        cssCustomProperties[setterConfig.target.styleItemId];

      if (!oldValue) {
        return null;
      }

      const targetDecomposed = decompose(
        setterConfig.target.cssPropertyType,
        oldValue,
      );

      if (!targetDecomposed) {
        return null;
      }

      targetDecomposed[setterConfig.target.subKey] = atomicValue;

      const composed = compose(
        setterConfig.target.cssPropertyType,
        targetDecomposed,
      );

      return composed
        ? { styleValue: composed, targetKey: setterConfig.target.styleItemId }
        : null;
    }
  }

  return null;
};

export const processStyleChanges = (
  newStyles: Record<string, string>,
  namespaceType: StyleNamespace,
  styleDefinitions: CssCustomProperties,
  setters: Record<StyleValueKey, SetterConfig[]>,
  themeVariables: Record<string, string> = {},
): Map<
  EditorPointer<CompRef>,
  { styleValues: StyleValues; type: ManifestComponentExtensionType }
> => {
  const changesMap = new Map<
    EditorPointer<CompRef>,
    { styleValues: StyleValues; type: ManifestComponentExtensionType }
  >();

  for (const [key, value] of Object.entries(newStyles)) {
    const valueCssPropertyType = (
      namespaceType === STYLE_NAMESPACE.CssCustomProperties
        ? styleDefinitions[key]?.cssPropertyType
        : key
    ) as CssPropertyType;

    if (!valueCssPropertyType) {
      continue;
    }

    if (!setters[valueCssPropertyType]) {
      continue;
    }

    for (const setterConfig of setters[valueCssPropertyType]) {
      if (!changesMap.has(setterConfig.target.editorPointer)) {
        changesMap.set(setterConfig.target.editorPointer, {
          styleValues: {
            cssProperties: {},
            cssCustomProperties: {},
          },
          type: setterConfig.target.context.getExtensionType(),
        });
      }

      const stylesResult = changesMap.get(setterConfig.target.editorPointer)!;
      const namespace =
        stylesResult.styleValues[setterConfig.target.styleNamespace];

      const styleChange = getStyleСhange(
        value,
        valueCssPropertyType,
        setterConfig,
        namespace,
        themeVariables,
      );

      if (styleChange?.styleValue && styleChange?.targetKey) {
        namespace[styleChange.targetKey] = styleChange.styleValue;
      }
    }
  }

  return changesMap;
};
